/*
 * Native cart notification (Dawn `CartNotification`) — hardened.
 *
 * Public surface is unchanged: renderContents / getSectionsToRender /
 * getSectionInnerHTML / setActiveElement / open / close / handleBodyClick are all
 * still here with the same names, and product-form.js and quick-add.js keep working.
 *
 * What changed and why:
 *  B1  the panel auto-dismisses 3.0s AFTER the open transition finishes (not 3.0s
 *      after open() is called), using the same CSS close animation as a manual close.
 *  B2  the timer is held while the pointer is over the panel or keyboard focus is
 *      inside it; leaving / blurring starts a fresh 3.0s.
 *  B3  dismiss listeners are bound on `document` in the CAPTURE phase (click,
 *      keydown-Escape and a movement-filtered touch tap) instead of a bubble-phase
 *      `document.body` click. The old binding was unreachable in two real cases:
 *      a third party calling stopPropagation before the event reached <body>, and
 *      iOS Safari not delivering taps on non-interactive elements to body-level
 *      delegates. Capture on `document` is upstream of both.
 *  B4  every open bumps an open token and clears every pending timer, so a stale
 *      timer from a previous notification can never close a fresh one early.
 *  B5a the `.animate` and `.active` class mutations are separated by a forced
 *      reflow, so the transition is triggered deterministically in every engine
 *      rather than relying on same-frame style-change resolution.
 *  B5c focus is taken with { preventScroll: true } and the scroll position is
 *      restored if anything moved it, and focus is only handed back on close when
 *      it was actually trapped inside the panel — no iOS scroll jump.
 *  B6  the component is inert (never throws) when #cart-notification is absent or
 *      hidden — e.g. while Rebuy Smart Cart owns the cart UI and the snippet applies
 *      `body.smart-cart--enabled cart-notification { display: none }`.
 *
 * The panel is also opened from outside this file (product-form.js adds the
 * `.active` class directly). A class MutationObserver adopts that case so the
 * dismiss listeners and the auto-close timer are installed no matter which path
 * opened the panel.
 */
class CartNotification extends HTMLElement {
  constructor() {
    super();

    /* ---- tunables ---- */
    this.autoCloseDelay = 2000; /* dwell time once the panel has finished opening */
    this.exitMs = 300; /* eased fade+lift out; matches .closing in CSS  */
    this.openFallbackDelay = 150; /* added to the measured transition, safety net  */
    this.dismissGraceMs = 60; /* ignore dismiss events fired as we open        */
    this.tapSlopPx = 10; /* touch movement above this is a scroll, not a tap */

    this.notification = document.getElementById("cart-notification");
    this.header = document.querySelector("sticky-header");

    this.isOpen = false;
    this.openToken = 0;
    this.openedAt = 0;
    this.autoCloseTimer = null;
    this.openFallbackTimer = null;
    this.exitTimer = null;
    this.exitToken = 0;
    this.exitEndHandler = null;
    this.finishedToken = -1;
    this.hoverHold = false;
    this.focusHold = false;
    this.touchStart = null;

    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.onTouchStart = this.handleTouchStart.bind(this);
    this.onTouchEnd = this.handleTouchEnd.bind(this);
    this.onPointerEnter = this.handlePointerEnter.bind(this);
    this.onPointerLeave = this.handlePointerLeave.bind(this);
    this.onFocusIn = this.handleFocusIn.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);

    /* B6 — no panel in the DOM: stay defined and callable, but do nothing. */
    if (!this.notification) return;

    this.notification.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close("escape")
    );
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener("click", () => this.close("button"))
    );

    /* B2 — hold handlers live for the element's lifetime; they are inert while closed. */
    this.notification.addEventListener("pointerenter", this.onPointerEnter);
    this.notification.addEventListener("pointerleave", this.onPointerLeave);
    this.notification.addEventListener("mouseenter", this.onPointerEnter);
    this.notification.addEventListener("mouseleave", this.onPointerLeave);
    this.notification.addEventListener("focusin", this.onFocusIn);
    this.notification.addEventListener("focusout", this.onFocusOut);

    /* Adopt opens/closes performed by other scripts flipping `.active` directly. */
    if (typeof MutationObserver === "function") {
      this.classObserver = new MutationObserver(() => this.syncOpenState());
      this.classObserver.observe(this.notification, {
        attributes: true,
        attributeFilter: ["class"],
      });
      this.syncOpenState();
    }
  }

  disconnectedCallback() {
    this.clearTimers();
    this.unbindDismissListeners();
    if (this.classObserver) this.classObserver.disconnect();
  }

  /* ------------------------------------------------------------------ open */

  open() {
    if (!this.notification) return;

    /* B4 — a fresh open never inherits a previous cycle's pending work. */
    this.clearTimers();

    /* A second add-to-cart while the panel is mid-exit: abandon the exit and reset to
       the closed base state, so the new panel slides in cleanly from the top rather
       than animating out of a half-faded, half-translated one. */
    const wasExiting = this.notification.classList.contains("closing");
    this.clearExit();
    if (wasExiting) {
      this.notification.classList.remove("closing", "active");
      void this.notification.offsetHeight;
    }

    const alreadyActive = this.notification.classList.contains("active");
    const previousCycleSettled = this.finishedToken === this.openToken;
    const token = ++this.openToken;
    this.isOpen = true;
    this.openedAt = Date.now();
    this.focusHold = false;

    /* B5a — two separate style mutations with a forced reflow in between, so the
       pre-transform state is committed before `.active` changes the transform. */
    this.notification.classList.add("animate");
    void this.notification.offsetHeight;
    this.notification.classList.add("active");

    this.bindDismissListeners();

    const onOpenEnd = (evt) => {
      if (evt.target !== this.notification) return;
      if (evt.propertyName && evt.propertyName !== "transform") return;
      this.notification.removeEventListener("transitionend", onOpenEnd);
      this.finishOpen(token);
    };
    this.notification.addEventListener("transitionend", onOpenEnd);

    /* Fallback: transitionend does not fire when the panel is hidden, when a
       reduced-motion / 0s duration applies, or if the transition is interrupted. */
    this.openFallbackTimer = setTimeout(() => {
      this.openFallbackTimer = null;
      this.notification.removeEventListener("transitionend", onOpenEnd);
      this.finishOpen(token);
    }, this.transitionMs() + this.openFallbackDelay);

    /* Re-open of an already-settled panel: no new transition can start, so settle on
       the next frame instead of burning the fallback delay. (If a transition is still
       in flight, previousCycleSettled is false and transitionend handles it.) */
    if (alreadyActive && previousCycleSettled) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.finishOpen(token))
      );
    }
  }

  /* Runs once per open, when the panel has finished animating in. */
  finishOpen(token) {
    if (token !== this.openToken || !this.isOpen) return; /* stale cycle */
    if (this.finishedToken === token) return; /* transitionend + fallback race */
    this.finishedToken = token;

    if (this.openFallbackTimer) {
      clearTimeout(this.openFallbackTimer);
      this.openFallbackTimer = null;
    }

    this.focusPanel();
    this.scheduleAutoClose();
  }

  /* B5c — take focus without letting the browser scroll the page to reach it. */
  focusPanel() {
    if (!this.isPanelVisible()) return;
    const x = window.pageXOffset;
    const y = window.pageYOffset;
    try {
      this.notification.focus({ preventScroll: true });
    } catch (e) {
      this.notification.focus();
    }
    if (typeof trapFocus === "function") trapFocus(this.notification);
    if (window.pageXOffset !== x || window.pageYOffset !== y) {
      window.scrollTo(x, y);
    }
  }

  /* ----------------------------------------------------------------- close */

  close(reason) {
    if (!this.notification) return;

    this.isOpen = false;
    this.clearTimers();
    this.unbindDismissListeners();
    this.hoverHold = false;
    this.focusHold = false;
    this.touchStart = null;

    this.startExit();

    this.releaseFocus();
  }

  /* ------------------------------------------------------------- eased exit */

  /* Every close path — auto-dismiss, X, Escape, outside click — routes through here,
     so they all share one exit animation. `.closing` fades the panel out and lifts it
     slightly (CSS), and makes it pointer-events:none the instant the exit starts.
     `.active` is deliberately held until the exit finishes, because it is what keeps
     `visibility: visible`; dropping it up front would hard-hide the panel and there
     would be nothing left to animate. Both classes come off together at the end, which
     returns the panel to the closed base state (fully hidden, opacity restored). */
  startExit() {
    if (!this.notification) return;
    this.clearExit();

    /* Never shown (or already fully closed): nothing to animate. */
    if (!this.notification.classList.contains("active")) {
      this.notification.classList.remove("closing");
      return;
    }

    const token = ++this.exitToken;
    this.notification.classList.add("closing");

    const onExitEnd = (evt) => {
      if (evt.target !== this.notification) return;
      if (evt.propertyName && evt.propertyName !== "opacity") return;
      this.finishExit(token);
    };
    this.exitEndHandler = onExitEnd;
    this.notification.addEventListener("transitionend", onExitEnd);

    /* Fallback for the cases transitionend never fires: reduced motion, a 0s
       duration, a hidden panel, or an interrupted transition. */
    this.exitTimer = setTimeout(
      () => this.finishExit(token),
      this.exitMs + this.openFallbackDelay
    );
  }

  finishExit(token) {
    if (token !== this.exitToken) return; /* superseded by a newer cycle */
    this.clearExit();
    /* `.animate` comes off with the rest. It delays `visibility` by --duration-short,
       so leaving it on would keep the panel `visible` for ~100ms after `.closing` (and
       with it opacity: 0) had already been removed — i.e. the fully-opaque panel would
       flash back for a frame before hiding. open() re-adds `.animate` every cycle. */
    this.notification.classList.remove("active", "closing", "animate");
  }

  /* Detach the in-flight exit without touching classes, so callers decide the
     resulting visual state (open() resets it, finishExit() completes it). */
  clearExit() {
    this.exitToken++;
    if (this.exitTimer) {
      clearTimeout(this.exitTimer);
      this.exitTimer = null;
    }
    if (this.exitEndHandler) {
      this.notification.removeEventListener("transitionend", this.exitEndHandler);
      this.exitEndHandler = null;
    }
  }

  releaseFocus() {
    const focusWasInside = !!(
      document.activeElement && this.notification.contains(document.activeElement)
    );

    /* Unbind the trap only — never let removeTrapFocus() move focus for us, because
       it focuses without preventScroll and would jump the page on an auto-close. */
    if (typeof removeTrapFocus === "function") removeTrapFocus();

    if (!focusWasInside) return; /* focus is elsewhere on the page: leave it alone */

    const x = window.pageXOffset;
    const y = window.pageYOffset;
    const target = this.activeElement;
    if (target && typeof target.focus === "function" && document.contains(target)) {
      try {
        target.focus({ preventScroll: true });
      } catch (e) {
        target.focus();
      }
    } else if (document.activeElement && document.activeElement.blur) {
      /* never leave focus parked on a now-hidden panel */
      document.activeElement.blur();
    }
    if (window.pageXOffset !== x || window.pageYOffset !== y) {
      window.scrollTo(x, y);
    }
  }

  /* ---------------------------------------------------------- auto-close */

  scheduleAutoClose() {
    this.clearAutoClose();
    if (!this.isOpen || !this.autoCloseDelay) return;
    if (this.hoverHold || this.focusHold || this.isHovered() || this.hasFocusInside()) return;
    this.autoCloseTimer = setTimeout(() => {
      this.autoCloseTimer = null;
      if (!this.isOpen) return;
      if (this.hoverHold || this.focusHold || this.isHovered() || this.hasFocusInside()) {
        return; /* held after the timer was armed: wait for the release to re-arm */
      }
      this.close("auto");
    }, this.autoCloseDelay);
  }

  clearAutoClose() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }

  clearTimers() {
    this.clearAutoClose();
    if (this.openFallbackTimer) {
      clearTimeout(this.openFallbackTimer);
      this.openFallbackTimer = null;
    }
  }

  /* --------------------------------------------------------------- holds */

  handlePointerEnter(evt) {
    if (!this.hoverHoldAllowed(evt)) return;
    this.hoverHold = true;
    this.clearAutoClose();
  }

  handlePointerLeave() {
    if (!this.hoverHold) return;
    this.hoverHold = false;
    this.scheduleAutoClose(); /* B2 — a full fresh 3.0s */
  }

  /* A touch device has no persistent pointer: iOS keeps :hover (and synthesised
     mouseenter) on the last-tapped element indefinitely, which would wedge the
     dwell timer open forever. Only honour a hover hold from a real pointing
     device, or from a finger/pen that is physically down on the panel (that one
     always terminates with a pointerleave on lift). */
  hoverHoldAllowed(evt) {
    const type = evt && evt.pointerType;
    if (type === "touch" || type === "pen") return true;
    return this.canHover();
  }

  canHover() {
    try {
      return window.matchMedia("(hover: hover)").matches;
    } catch (e) {
      return true;
    }
  }

  handleFocusIn(evt) {
    if (evt.target === this.notification) return; /* our own programmatic focus */
    this.focusHold = true;
    this.clearAutoClose();
  }

  handleFocusOut() {
    /* focusout fires before the new focus lands, so re-check on the next tick. */
    setTimeout(() => {
      this.focusHold = this.hasFocusInside();
      if (!this.focusHold) this.scheduleAutoClose();
    }, 0);
  }

  hasFocusInside() {
    const a = document.activeElement;
    return !!a && a !== this.notification && this.notification.contains(a);
  }

  isHovered() {
    try {
      return this.canHover() && this.notification.matches(":hover");
    } catch (e) {
      return false;
    }
  }

  /* ------------------------------------------------------------- dismiss */

  bindDismissListeners() {
    if (this.dismissBound) return;
    this.dismissBound = true;
    /* Capture on `document`: upstream of any stopPropagation, and reached by iOS
       taps that never make it to a bubble-phase `document.body` delegate. */
    document.addEventListener("click", this.onBodyClick, true);
    document.addEventListener("keydown", this.onDocumentKeydown, true);
    document.addEventListener("touchstart", this.onTouchStart, { capture: true, passive: true });
    document.addEventListener("touchend", this.onTouchEnd, { capture: true, passive: true });
  }

  unbindDismissListeners() {
    if (!this.dismissBound) return;
    this.dismissBound = false;
    document.removeEventListener("click", this.onBodyClick, true);
    document.removeEventListener("keydown", this.onDocumentKeydown, true);
    document.removeEventListener("touchstart", this.onTouchStart, { capture: true });
    document.removeEventListener("touchend", this.onTouchEnd, { capture: true });
  }

  handleDocumentKeydown(evt) {
    const key = evt.key || evt.code;
    if (key === "Escape" || key === "Esc" || evt.keyCode === 27) this.close("escape");
  }

  handleTouchStart(evt) {
    const t = evt.changedTouches && evt.changedTouches[0];
    this.touchStart = t
      ? { x: t.clientX, y: t.clientY, at: Date.now(), target: evt.target }
      : null;
  }

  handleTouchEnd(evt) {
    const start = this.touchStart;
    this.touchStart = null;
    if (!start) return;
    const t = evt.changedTouches && evt.changedTouches[0];
    if (!t) return;
    /* only a genuine tap dismisses — a drag is a scroll and must not close */
    if (Math.abs(t.clientX - start.x) > this.tapSlopPx) return;
    if (Math.abs(t.clientY - start.y) > this.tapSlopPx) return;
    if (Date.now() - start.at > 700) return;
    this.dismissIfOutside(evt.target || start.target);
  }

  handleBodyClick(evt) {
    this.dismissIfOutside(evt.target);
  }

  dismissIfOutside(target) {
    if (!this.isOpen || !target) return;
    if (Date.now() - this.openedAt < this.dismissGraceMs) return;
    if (target === this.notification) return;
    if (typeof target.closest !== "function") return;
    if (target.closest("cart-notification")) return;

    const disclosure = target.closest("details-disclosure, header-menu");
    this.activeElement = disclosure ? disclosure.querySelector("summary") : null;
    this.close("outside");
  }

  /* ------------------------------------------------------------ plumbing */

  /* Longest transition currently declared on the panel, in ms. */
  transitionMs() {
    try {
      const cs = getComputedStyle(this.notification);
      const durations = String(cs.transitionDuration || "0s").split(",");
      const delays = String(cs.transitionDelay || "0s").split(",");
      let max = 0;
      for (let i = 0; i < durations.length; i++) {
        const d = (parseFloat(durations[i]) || 0) * 1000;
        const l = (parseFloat(delays[i % delays.length]) || 0) * 1000;
        if (d + l > max) max = d + l;
      }
      return max;
    } catch (e) {
      return 200;
    }
  }

  isPanelVisible() {
    try {
      if (getComputedStyle(this).display === "none") return false;
      if (getComputedStyle(this.notification).display === "none") return false;
      return this.notification.getClientRects().length > 0;
    } catch (e) {
      return true;
    }
  }

  /* Keeps us in step with anything that toggles `.active` directly. */
  syncOpenState() {
    if (!this.notification) return;
    /* During the eased exit the panel deliberately keeps `.active` (that is what holds
       `visibility: visible` while it fades) and carries `.closing`. Those are our own
       class mutations, not a third party opening the panel — without this guard the
       observer reads `.active` with isOpen already false, calls open() again, and the
       panel re-opens on a loop and never dismisses. */
    if (this.notification.classList.contains("closing")) return;
    const active = this.notification.classList.contains("active");
    if (active && !this.isOpen) this.open();
    else if (!active && this.isOpen) this.close("external");
  }

  /* -------------------------------------------------- Dawn public surface */

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      if (!target) return;
      const source = parsedState.sections && parsedState.sections[section.id];
      if (!source) return;
      const html = this.getSectionInnerHTML(source, section.selector);
      if (html !== null) target.innerHTML = html;
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-notification-product",
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: "cart-notification-button",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const found = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
    return found ? found.innerHTML : null;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get("cart-notification")) {
  customElements.define("cart-notification", CartNotification);
}
